import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, XCircle, AlertCircle, RefreshCw, Database, FileText, BookOpen } from 'lucide-react';

interface CourseHealth {
  id: string;
  title: string;
  category: string;
  module_count: number;
  content_count: number;
  status: 'healthy' | 'warning' | 'critical';
}

export default function CourseHealthDashboard() {
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState<CourseHealth[]>([]);
  const [stats, setStats] = useState({
    totalCourses: 0,
    healthyCourses: 0,
    warningCourses: 0,
    criticalCourses: 0,
    totalModules: 0,
    modulesWithContent: 0
  });

  const checkHealth = async () => {
    setLoading(true);
    try {
      // Get all active courses with module and content counts
      const { data: courses, error } = await supabase
        .from('courses')
        .select(`
          id,
          title,
          category,
          total_modules,
          course_modules!inner(
            id,
            module_content(id)
          )
        `)
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('title', { ascending: true });

      if (error) throw error;

      const healthResults: CourseHealth[] = [];
      let totalModules = 0;
      let modulesWithContent = 0;

      courses?.forEach((course: any) => {
        const moduleCount = course.course_modules?.length || 0;
        const contentCount = course.course_modules?.filter((m: any) => m.module_content?.length > 0).length || 0;

        totalModules += moduleCount;
        modulesWithContent += contentCount;

        let status: 'healthy' | 'warning' | 'critical' = 'healthy';
        if (moduleCount === 0) {
          status = 'critical';
        } else if (contentCount < moduleCount) {
          status = 'warning';
        }

        healthResults.push({
          id: course.id,
          title: course.title,
          category: course.category,
          module_count: moduleCount,
          content_count: contentCount,
          status
        });
      });

      setHealthData(healthResults);
      setStats({
        totalCourses: healthResults.length,
        healthyCourses: healthResults.filter(c => c.status === 'healthy').length,
        warningCourses: healthResults.filter(c => c.status === 'warning').length,
        criticalCourses: healthResults.filter(c => c.status === 'critical').length,
        totalModules,
        modulesWithContent
      });
    } catch (error) {
      console.error('Error checking course health:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      healthy: 'default',
      warning: 'secondary',
      critical: 'destructive'
    };
    return (
      <Badge variant={variants[status]}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Course Data Health Dashboard</h2>
        <p className="text-muted-foreground">
          Monitor course data integrity and content completeness
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Courses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">{stats.totalCourses}</div>
              <Database className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="mt-2 flex gap-2">
              <Badge variant="default" className="bg-green-500">
                {stats.healthyCourses} Healthy
              </Badge>
              <Badge variant="secondary">
                {stats.warningCourses} Warning
              </Badge>
              <Badge variant="destructive">
                {stats.criticalCourses} Critical
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Module Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">{stats.totalModules}</div>
              <BookOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {stats.modulesWithContent} modules have content (
              {stats.totalModules > 0 ? Math.round((stats.modulesWithContent / stats.totalModules) * 100) : 0}%)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Content Coverage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">
                {stats.totalModules > 0 ? Math.round((stats.modulesWithContent / stats.totalModules) * 100) : 0}%
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Overall content completeness rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button onClick={checkHealth} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh Health Check
        </Button>
      </div>

      {/* Critical Issues Alert */}
      {stats.criticalCourses > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Critical Issues Found:</strong> {stats.criticalCourses} course(s) have no modules.
            Please run the Course Sync Utility to populate missing data.
          </AlertDescription>
        </Alert>
      )}

      {/* Warning Issues Alert */}
      {stats.warningCourses > 0 && (
        <Alert className="border-yellow-500 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Warning:</strong> {stats.warningCourses} course(s) have modules missing content.
            Review the list below and sync missing content.
          </AlertDescription>
        </Alert>
      )}

      {/* Course Health List */}
      <Card>
        <CardHeader>
          <CardTitle>Course Health Details</CardTitle>
          <CardDescription>
            Detailed view of all courses and their data integrity status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {healthData.map((course) => (
              <div
                key={course.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  {getStatusIcon(course.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{course.title}</h3>
                      {getStatusBadge(course.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Category: {course.category} • ID: {course.id}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">
                    {course.module_count} Module{course.module_count !== 1 ? 's' : ''}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {course.content_count} with content
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
